import { Box, Text, useDisclosure, useToast } from '@chakra-ui/react';
import { ModalBuild } from 'components/molecules/ModalBuild';
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces';
import { TableBuild } from 'components/organisms';
import { motion } from 'framer-motion';
import {
    ForwardRefRenderFunction,
    forwardRef,
    memo,
    useEffect,
    useImperativeHandle,
    useRef,
    useState
} from 'react';
import ContractService from 'services/contracts';
import formatter from 'utils/formatter';
import { removeEmptyValues } from 'utils/removeEmptyValues';
import { defaultLoader, vehicleReportHeaders } from '../../constants';
import { Vehicle, VehicleFilter } from '../../types';
import { VehicleCreateModal } from '../Create/VehicleCreateModal';


interface IPropsModelTable {
  filter: VehicleFilter;
}

interface IModeTableRef {
  resetPage: () => void;
}

const ModeTableFC: ForwardRefRenderFunction<IModeTableRef, IPropsModelTable> = (
  { filter },
  refModeTable
) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState(10);
  const [table, setTable] = useState({ ...vehicleReportHeaders, body: [] });
  const [vehicleEdit, setVehicleEdit] = useState<Vehicle | null>(null);
  const [vehicleDeleteGuid, setVehicleDeleteGuid] = useState<string | null>(null);

  const { isOpen, onOpen, onClose } = useDisclosure(); // Modal de inclusão
  const editModal = useDisclosure(); // Modal de edição
  const deleteModal = useDisclosure(); // Modal de exclusão

  useImperativeHandle(refModeTable, () => ({
    resetPage: () => setPage(1)
  }));

    const buildTableBody = (vehicles: Vehicle[]) => {
    return vehicles.map((vehicle) => ({
        id: vehicle.guid,
        ...vehicle
    }));
    };

  const buildPayload = () => {
    return removeEmptyValues({
      pagina: page,
      limite: limit,
      ...filter
    });
  };

  async function fetchVehicles() {
    setLoading(true);
    try {
      const response = await ContractService.getVehicles(buildPayload());
      setTotalPages(+response.headers['total-pages'] || 1);
      const responseData = response.data || [];

      const mappedVehicles: Vehicle[] = responseData.map((item: any) => ({
        guid: item.guid,
        marca: item.marca,
        modelo: item.modelo,
        ativo: item.ativo ? 'Sim' : 'Não',
        dataCriacao: formatter.data(item.dataCriacao),
        dataAlteracao: formatter.data(item.dataAlteracao)
      }));

      setTable((old: any) => ({
        ...old,
        body: buildTableBody(mappedVehicles)
      }));
    } catch (error: any) {
        const response = error.response;
        const errorMessage =
        Array.isArray(response?.data)
        ? response.data[0]?.mensagem
        : response?.data?.mensagem || response?.data?.message || 
            'Erro ao buscar veículos.';

        toast({
            title: 'Erro ao buscar veículos.',
            description: errorMessage,
            status: 'error',
            position: 'bottom-left',
            isClosable: true
        });
      setTable((old) => ({ ...old, body: [] }));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchVehicles();
  }, [page, limit, filter]);

  useEffect(() => {
    const handleOpenCreate = () => onOpen();
    window.addEventListener('openVehicleCreateModal', handleOpenCreate);
    return () => window.removeEventListener('openVehicleCreateModal', handleOpenCreate);
  }, []);

  const handlePage = (value: number) => {
    if (page !== value) setPage(value);
  };

  const handleLimit = (value: number) => {
    setLimit(value);
    setPage(1);
  };

  // ===== AÇÕES DE EDIÇÃO E EXCLUSÃO =====
    const handleAction = (typeAction: string, guid: string) => {
    const selected = table.body.find((v: any) => v.guid === guid);
    if (!selected) return;

    if (typeAction === 'EDIT') {
        setVehicleEdit(selected);
        editModal.onOpen();
    }

    if (typeAction === 'DELETE') {
        setVehicleDeleteGuid(guid);
        toggleDeleteModal(true); // 👈 agora realmente abre o modal
    }
    };

  const handleDelete = async () => {
    if (!vehicleDeleteGuid) return;

    try {
      await ContractService.deleteVehicle(vehicleDeleteGuid);
      toast({
        title: 'Veículo removido com sucesso!',
        position: 'bottom-left',
        status: 'success',
        isClosable: true
      });
      fetchVehicles();
    } catch (error: any) {
        const response = error.response;
        const errorMessage =
        Array.isArray(response?.data)
        ? response.data[0]?.mensagem
        : response?.data?.mensagem || response?.data?.message || 
            'Erro ao excluir veículo.';

        toast({
            title: 'Erro ao excluir veículo.',
            description: errorMessage,
            status: 'error',
            position: 'bottom-left',
            isClosable: true
        });
    } finally {
        setVehicleDeleteGuid(null);
        toggleDeleteModal(false); // 👈 fecha corretamente o modal via ref
    }
  };

  const deleteModalRef = useRef<ModalBuildHandle>(null);

    const toggleDeleteModal = (open = true) => {
    if (open) {
        deleteModalRef.current?.onOpen();
    } else {
        deleteModalRef.current?.onClose();
    }
    };

  return (
    <Box as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
      {/* Tabela */}
      <TableBuild
        header={table.header}
        body={table.body}
        loading={{
          status: loading,
          loader: defaultLoader
        }}
        pagination={{
          page,
          limit,
          totalPages
        }}
        onChangePage={handlePage}
        onChangeLimit={handleLimit}
        // Aqui adicionamos os botões de ação
        action={[
          { type: 'EDIT' },
          { type: 'DELETE' }
        ]}
        onAction={(type, id) => handleAction(type, String(id))}
      />

      {/* Modal de criação */}
      <VehicleCreateModal
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={fetchVehicles}
      />

      {/* Modal de edição (reutiliza o mesmo formulário de criação) */}
      <VehicleCreateModal
        isOpen={editModal.isOpen}
        onClose={() => {
          setVehicleEdit(null);
          editModal.onClose();
        }}
        onSuccess={() => {
          editModal.onClose();
          fetchVehicles();
        }}
        vehicleEdit={vehicleEdit}
      />

      {/* Modal de exclusão */}
        <ModalBuild
        ref={deleteModalRef}
        closeOnOverlayClick={false}
        title="Confirmação de exclusão"
        size="md"
        actions={{
            position: 'flex-end',
            childrens: [
            {
                text: 'Cancelar',
                handle: 'cancel',
                variant: 'outline'
            },
            {
                text: 'Excluir',
                handle: 'confirm',
                colorScheme: 'red'
            }
            ]
        }}
        onConfirm={handleDelete}
        onCancel={() => toggleDeleteModal(false)}
        >
        <Text px={6} py={4}>
            Deseja realmente excluir este veículo? Esta ação não poderá ser desfeita.
        </Text>
    </ModalBuild>
    </Box>
  );
};

export const ModeTable = memo(forwardRef(ModeTableFC));
